import { centsToDollars, dollarsToCents, formatAmount } from '@core/util/money';
import {
	AddressFormattingSettings,
	NameFormattingSettings,
} from '@models/businessSettings/settings';
import { Business, BusinessAddress, BusinessUser } from '@models/businesses';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import { Jurisdiction } from '@models/jurisdictions';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	Matter,
	MatterFileType,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';

export abstract class BaseFormMapper<T = unknown> {
	private FormToCloserMappings: { [id: string]: string };

	getCloserMappings(): T {
		return this.CloserToFormMappings;
	}

	abstract CloserToFormMappings: T;
	abstract MapValues(): unknown;
	abstract mergeFormData(
		formData: T,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage?: MatterMortgageWithAssociations,
		businessUsers?: BusinessUser[],
		primaryJurisdiction?: Jurisdiction,
		signors?: PersonMatterClientWithAssociations[],
		otherPartiesWithAssociations?: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterFileType?: MatterFileType,
		addressFormattingSettings?: AddressFormattingSettings,
	): T;

	GetFormFieldToCloserMapping(formField: string): string {
		if (!this.FormToCloserMappings) {
			this.FormToCloserMappings = Object.entries(
				this.CloserToFormMappings,
			).reduce((prev, [key, value]) => {
				prev[value] = key;

				return prev;
			}, {});
		}

		return this.FormToCloserMappings[formField];
	}

	getClientsName(client: PersonMatterClient | BusinessMatterClient) {
		if (client?.type === ClientType.PERSON) {
			return `${(client as PersonClient)?.lastName}, ${(client as PersonClient)?.firstName} ${(client as PersonClient)?.middleNames}`;
		}

		return (client as BusinessClient)?.name;
	}

	formValueToCents(value: string) {
		const removeSpace = value?.replace(/[ ]{2,}/g, '.') ?? '';

		return dollarsToCents(removeSpace);
	}

	toFormDollarPrice(value: number | string, spaceCount = 4) {
		const dollars = centsToDollars(value);
		const formatDollars = formatAmount(dollars);
		// eslint-disable-next-line prefer-spread
		const spaces = Array.apply(null, Array(spaceCount));

		return formatDollars
			? `$${formatDollars?.replace('.', spaces.join(' '))}`
			: '';
	}
}
